// NPM
import React, { useEffect, useState } from 'react'
import { useRecoilState } from "recoil";
import { useTranslation } from 'react-i18next';
import { isNil } from "lodash";
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// COMPONENT
import ProductDetails from 'Components/product/product-details'

// ATOM
import { inventoryAtom } from "Recoil/inventory.atom";
import { CartAtom } from 'Recoil/order.atom';

// SERVICE
import { fetchProductData } from "Services/inventory.services";
import { addToCartList, updateCartItem, fetchCartList } from 'Services/order.services';

// HELPERS
import { decode } from 'Helpers/enc';
import { toaster, encode } from 'Helpers/index';

// ANT COMPONENT
import { Badge } from 'antd';

// HOOK
import useSession from 'Hooks/session.hook';
import { statusChatAtom } from 'Recoil/chat.atom';
import classNames from 'classnames';
import './product-details.scss';

interface ProductDetailsPageProps {

}

const ProductDetailsPage: React.FC<ProductDetailsPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const inventoryID = decode(id);
  const { t } = useTranslation();
  const [carListItems, setCarListItems] = useState({});

  const [inventoryState, setInventoryState] = useRecoilState<any>(inventoryAtom);

  const [cartState, setCartState] = useRecoilState<any>(CartAtom);

  const { profile } = useSession()
  const [chatStatusState, setChatStatusState] = useRecoilState<any>(statusChatAtom);


  useEffect(() => {
    if (profile !== undefined) {
      fetchCartListItems()
    }
    fetchProduct()
    setChatStatusState({
      ...chatStatusState,
      status: { ...chatStatusState.status, supplier: false, product: true, order: false }
    });
  }, [inventoryID])


  const updateCart = async (data) => {
    setCartState({ ...cartState, status: { ...cartState.status, update: 'loading' } });

    const responseData = await updateCartItem(data);
    if (!isNil(responseData) && responseData.updated) {
      setCartState({
        ...cartState,
        status: { ...cartState.status, update: 'success' },
      });
      toaster('success', t('toaster.cartUpdateSuccess'))
      fetchCartListItems()
    }
    else {
      setCartState({
        ...cartState,
        status: { ...cartState.status, update: 'error' }
      });

      toaster('error', t('toaster.addCartFailed'))
    }
  }

  const addToCart = async (data) => {
    setCartState({ ...cartState, status: { ...cartState.status, create: 'loading' } });

    const responseData = await addToCartList(data);

    if (!isNil(responseData) && responseData.success) {
      setCartState({
        ...cartState,
        status: { ...cartState.status, create: 'success' },
      });
      toaster('success', t('toaster.cartAddSuccess'))
      fetchCartListItems()
    }
    else {
      setCartState({
        ...cartState,
        status: { ...cartState.status, create: 'error' }
      });
      toaster('error', t('toaster.addCartFailed'))
    }
  }

  const fetchProduct = async () => {
    setInventoryState({ ...inventoryState, status: { ...inventoryState.status, fetch: 'loading' } });

    const responseData = await fetchProductData({ inventoryID: inventoryID });


    if (!isNil(responseData)) {
      setInventoryState({
        ...inventoryState,
        status: { ...inventoryState.status, fetch: 'success' },
        data: responseData
      });

      // setAllInventoryList(responseData)
    }
    else {
      setInventoryState({
        ...inventoryState,
        status: { ...inventoryState.status, fetch: 'error' }
      });
    }
  }


  const fetchCartListItems = async () => {
    setCartState({ ...cartState, status: { ...cartState.status, fetch: 'loading' } });

    const responseData = await fetchCartList();

    if (!isNil(responseData) && responseData.cartID) {
      setCartState({
        ...cartState,
        status: { ...cartState.status, fetch: 'success' },
        list: responseData.items
      });
      setCarListItems(responseData)
    }
    else {
      setCartState({
        ...cartState,
        status: { ...cartState.status, fetch: 'error' }
      });

      // toaster('error', responseData.error.message)
    }
  }


  const refreshData = () => {
    fetchCartListItems()
    // fetchProduct()  // no need to fetch same product again when refreshing (after login successfully)
  }

  return (

    <div className="product-details-page ">
      <Helmet>
        <title>{inventoryState?.data?.product?.productName || t('pageTitle.productDetails')}</title>
        <meta name="description" content={inventoryState?.data?.product?.productName}/>
        {inventoryState?.data?.description ? <meta name="description" content={inventoryState?.data?.description.replace(/<\/?[^>]+>/gi, ' ').slice(0,169)}/> : null}
        {inventoryState?.data?.ecomDetails ? <meta name="description" content={inventoryState?.data?.ecomDetails.replace(/<\/?[^>]+>/gi, ' ').slice(0,169)}/> : null}
        {inventoryState?.data?.disclaimer ? <meta name="description" content={inventoryState?.data?.disclaimer.replace(/<\/?[^>]+>/gi, ' ').slice(0,169)}/> : null}
        {inventoryState?.data?.specification ? <meta name="description" content={inventoryState?.data?.specification.replace(/<\/?[^>]+>/gi, ' ').slice(0,169)}/> : null}
        <link rel="canonical" href={`/products/${inventoryID}?name=${inventoryState?.data?.product?.productName}`}/>
      </Helmet>

      <div className="gradient-bg" />

      <Badge.Ribbon
        placement='start'
        color='#FFCB3F'
        text={
          <span className="ribbon-text">{t('badge.bestSellerInMarket')}</span>
        }
        className={classNames({ hideBestSeller: inventoryState?.data?.product?.productType ===  'NO_SHIPPING' })}
      >
        <div className="page--container">
          <ProductDetails
            handleUpdateToCart={updateCart}
            handleAddToCart={addToCart}
            cartList={carListItems}
            productData={inventoryState.data}
            handleFetchCart={refreshData}
          />
        </div>
      </Badge.Ribbon>
    </div>
  )
}

export default ProductDetailsPage;
